import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Modal,
  useWindowDimensions
} from 'react-native';
import { useTheme } from '../contexts/ThemeContext';// Removed react-beautiful-dnd - using simpler up/down buttons instead
import { auth } from '../firebaseConfig';
import { getTemplates, getUserPreferences } from '../services/firestoreService';
import { generateStructuredTasks, convertToNotionFormat, applySuggestionToTasks } from '../services/openaiService';
import { analyzeTaskList } from '../services/aiAssistantService';
import LandingStyleHeader from '../components/LandingStyleHeader';
import Toast from '../components/Toast';

export default function TaskManagerScreen({ navigation }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  // Fixed scrolling structure

  const [step, setStep] = useState(1); // 1: input, 2: template (optional), 3: result
  const [inputMode, setInputMode] = useState('tasks'); // 'tasks' or 'conversation'
  const [rawTaskInput, setRawTaskInput] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [tasks, setTasks] = useState([]); // Structured task objects for sidebar
  const [formattedMarkdown, setFormattedMarkdown] = useState(''); // Markdown output for center
  const [streamingMarkdown, setStreamingMarkdown] = useState(''); // Real-time streaming output
  const [loading, setLoading] = useState(false);
  const [loomOverview, setLoomOverview] = useState(''); // Main overview video
  const [strictAdherence, setStrictAdherence] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [strictAdherenceDefault, setStrictAdherenceDefault] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true); // Toggle left sidebar visibility
  const [showAISidebar, setShowAISidebar] = useState(true); // Toggle right AI sidebar
  const [dismissedSuggestions, setDismissedSuggestions] = useState([]); // Track dismissed AI suggestions
  const [aiSuggestions, setAiSuggestions] = useState([]); // AI-generated suggestions
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [taskListHeader, setTaskListHeader] = useState(''); // Page title/header
  const [overallDueDate, setOverallDueDate] = useState(''); // Single due date for entire list
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
  };

  // Define functions before useEffect
  const loadTemplates = async () => {
    try {
      const allTemplates = await getTemplates();
      // Filter for task manager templates only
      const taskTemplates = allTemplates.filter(t => t.templateType === 'task');
      setTemplates(taskTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadUserPreferences = async () => {
    try {
      const preferences = await getUserPreferences();
      setStrictAdherenceDefault(preferences.strictAdherenceDefault || false);
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  useEffect(() => {
    document.title = 'Flomodo - Task Manager';
    loadTemplates();
    loadUserPreferences();
  }, []);

  const handleGenerate = async () => {
    if (!rawTaskInput.trim()) {
      alert('Please enter some tasks');
      return;
    }

    setLoading(true);
    setStreamingMarkdown(''); // Clear previous stream
    setStep(3); // Jump to result screen immediately for streaming
    
    try {
      // Streaming callback to update UI in real-time
      const onStream = (partialResponse) => {
        try {
          // Try to parse partial JSON to extract formattedMarkdown
          const match = partialResponse.match(/"formattedMarkdown":\s*"([^"]*)"/);
          if (match) {
            const escaped = match[1];
            // Unescape the markdown
            const unescaped = escaped.replace(/\\n/g, '\n').replace(/\\"/g, '"');
            setStreamingMarkdown(unescaped);
          }
        } catch (e) {
          // Ignore parsing errors during streaming
        }
      };

      const result = await generateStructuredTasks(
        rawTaskInput,
        selectedTemplate?.content || null,
        strictAdherence,
        inputMode,
        onStream // Pass streaming callback
      );
      
      // Set final results
      setTasks(result.tasks || []);
      setFormattedMarkdown(result.formattedMarkdown || '');
      setTaskListHeader(result.header || 'Task List');
      setOverallDueDate(result.dueDate || '');
      setStreamingMarkdown(''); // Clear streaming state
      
      // Generate AI suggestions based on the tasks (don't block UI)
      if (result.tasks && result.tasks.length > 0) {
        setLoadingSuggestions(true);
        analyzeTaskList(result.tasks, rawTaskInput)
          .then(suggestions => {
            setAiSuggestions(suggestions);
            setDismissedSuggestions([]); // Reset dismissed suggestions
          })
          .catch(err => {
            console.error('Failed to generate AI suggestions:', err);
            setAiSuggestions([]);
          })
          .finally(() => {
            setLoadingSuggestions(false);
          });
      }
    } catch (error) {
      console.error('Task generation error:', error);
      alert('Error generating tasks: ' + error.message);
      setStep(2); // Go back on error
    } finally {
      setLoading(false);
    }
  };

  const addNewTask = () => {
    const newTask = {
      id: `task-${Date.now()}`,
      description: 'New task',
      level: 0,
      priority: 'medium',
      dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      estimatedHours: 2,
      completed: false,
      assignedTo: null,
      notes: '',
      parentId: null,
      children: []
    };
    setTasks([...tasks, newTask]);
    setEditingTaskId(newTask.id);
  };

  // Real-time update markdown when tasks change
  useEffect(() => {
    if (step === 3 && tasks.length > 0) {
      regenerateMarkdownFromTasks();
    }
  }, [tasks, taskListHeader]); // Watch tasks and header for changes

  const regenerateMarkdownFromTasks = () => {
    // Convert task structure back to markdown for display
    let markdown = '';
    
    // Don't include loom overview in markdown anymore - it's in the UI
    // Add Task Overview header
    markdown += '# Task Overview\n\n';
    
    // Generate task markdown WITHOUT due dates/hours/priority (those are in sidebar)
    markdown += generateMarkdownFromTasksList(tasks);
    setFormattedMarkdown(markdown);
  };

  const generateMarkdownFromTasksList = (taskList, level = 0) => {
    let markdown = '';
    taskList.forEach(task => {
      if (task.description) {
        const indent = '  '.repeat(level);
        
        // Check if description starts with heading or special marker
        if (task.description.trim().startsWith('#')) {
          markdown += `${task.description}\n\n`;
        } else {
          // Just the task description, no metadata
          markdown += `${indent}- ${task.description}\n`;
          
          // Add notes as nested item if present
          if (task.notes && task.notes.trim()) {
            const noteIndent = '  '.repeat(level + 1);
            markdown += `${noteIndent}- **NOTE**: ${task.notes}\n`;
          }
        }
        
        if (task.children && task.children.length > 0) {
          markdown += generateMarkdownFromTasksList(task.children, level + 1);
        }
      }
    });
    return markdown;
  };

  const updateTask = (taskId, updates) => {
    const updateTaskRecursive = (taskList) => {
      return taskList.map(task => {
        if (task.id === taskId) {
          return { ...task, ...updates };
        }
        if (task.children && task.children.length > 0) {
          return { ...task, children: updateTaskRecursive(task.children) };
        }
        return task;
      });
    };
    setTasks(updateTaskRecursive(tasks));
  };

  const deleteTask = (taskId) => {
    if (confirm('Delete this task?')) {
      const deleteTaskRecursive = (taskList) => {
        return taskList.filter(task => {
          if (task.id === taskId) return false;
          if (task.children && task.children.length > 0) {
            task.children = deleteTaskRecursive(task.children);
          }
          return true;
        });
      };
      setTasks(deleteTaskRecursive(tasks));
    }
  };

  const toggleComplete = (taskId) => {
    const task = findTaskById(tasks, taskId);
    if (task) {
      updateTask(taskId, { completed: !task.completed });
    }
  };

  const findTaskById = (taskList, taskId) => {
    for (const task of taskList) {
      if (task.id === taskId) return task;
      if (task.children && task.children.length > 0) {
        const found = findTaskById(task.children, taskId);
        if (found) return found;
      }
    }
    return null;
  };

  // Move task up/down in list
  const moveTask = (index, direction) => {
    const newTasks = [...tasks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newTasks.length) return;
    
    // Swap tasks
    [newTasks[index], newTasks[targetIndex]] = [newTasks[targetIndex], newTasks[index]];
    setTasks(newTasks);
  };

  // AI Suggestion handlers
  const handleDismissSuggestion = (suggestionId) => {
    setDismissedSuggestions([...dismissedSuggestions, suggestionId]);
  };

  const handleApplySuggestion = async (suggestionId, suggestion) => {
    try {
      setLoading(true);
      
      // Use AI to intelligently apply the suggestion to tasks
      const result = await applySuggestionToTasks(tasks, suggestion, rawTaskInput);
      
      // Update tasks with AI-modified version
      setTasks(result.tasks);
      
      // Show what was changed
      alert(`✅ Applied: ${result.changesApplied}`);
      
      // Dismiss the suggestion
      handleDismissSuggestion(suggestionId);
      
    } catch (error) {
      console.error('Error applying suggestion:', error);
      alert('Failed to apply suggestion. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setRawTaskInput('');
    setSelectedTemplate(null);
    setTasks([]);
    setFormattedMarkdown('');
    setStreamingMarkdown('');
    setLoomOverview('');
    setEditingTaskId(null);
    setTaskListHeader('');
    setOverallDueDate('');
    setAiSuggestions([]);
    setDismissedSuggestions([]);
  };

  const copyToClipboard = (format = 'markdown') => {
    let textToCopy;
    
    if (format === 'notion') {
      textToCopy = convertToNotionFormat(formattedMarkdown);
    } else {
      textToCopy = formattedMarkdown;
    }

    if (navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy);
      showToast(format === 'notion' ? 'Notion format copied!' : 'Tasks copied to clipboard!', 'success');
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
        <LandingStyleHeader navigation={navigation} currentScreen="tasks" />
        <View style={[styles.loadingContainer, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
          <ActivityIndicator size="large" color="#4a9eff" />
          <Text style={styles.loadingText}>Generating organized task list...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundImage: gradients.background }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="tasks" />
      {step > 1 && (
        <TouchableOpacity onPress={handleReset} style={styles.resetButton}>
          <Text style={styles.resetButtonText}>Start Over</Text>
        </TouchableOpacity>
      )}

      <ScrollView 
        style={[styles.content, { backgroundColor: colors.primary }]} 
        contentContainerStyle={step === 3 ? styles.contentContainerFull : styles.contentContainer}
        onScroll={(e) => {
          // Manually dispatch scroll event to window so header can detect it
          const scrollY = e.nativeEvent.contentOffset.y;
          window.lastKnownScrollY = scrollY;
          window.dispatchEvent(new Event('scroll'));
        }}
        scrollEventThrottle={16}
      >
        {/* STEP 1: Task Input */}
        {step === 1 && (
          <View style={styles.stepContainer}>
            <Text style={[styles.stepTitle, { color: colors.text }]}>Step 1: Enter Tasks</Text>
            <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
              Enter your tasks or paste an entire conversation - AI will extract and organize actionable tasks.
            </Text>

            {/* Input Mode Toggle */}
            <View style={styles.inputModeContainer}>
              <TouchableOpacity
                style={[
                  styles.inputModeButton,
                  { backgroundColor: isDark ? colors.tertiary : colors.card, borderColor: colors.border },
                  inputMode === 'tasks' && styles.inputModeButtonActive
                ]}
                onPress={() => setInputMode('tasks')}
              >
                <Text style={[
                  styles.inputModeButtonText,
                  { color: colors.textSecondary },
                  inputMode === 'tasks' && styles.inputModeButtonTextActive
                ]}>
                  Task List
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.inputModeButton,
                  { backgroundColor: isDark ? colors.tertiary : colors.card, borderColor: colors.border },
                  inputMode === 'conversation' && styles.inputModeButtonActive
                ]}
                onPress={() => setInputMode('conversation')}
              >
                <Text style={[
                  styles.inputModeButtonText,
                  { color: colors.textSecondary },
                  inputMode === 'conversation' && styles.inputModeButtonTextActive
                ]}>
                  Conversation
                </Text>
              </TouchableOpacity>
            </View>

            <Text style={[styles.inputModeHint, { color: colors.textSecondary }]}>
              {inputMode === 'tasks' 
                ? 'Enter tasks in any format - AI will organize and structure them'
                : 'Paste Slack, text, or email conversation - AI will extract all action items and tasks'}
            </Text>

            <TextInput
              style={[styles.textArea, { backgroundColor: isDark ? colors.tertiary : colors.card, borderColor: colors.border, color: colors.text }]}
              placeholder={inputMode === 'tasks'
                ? "# Task Overview\n\n- Main task\n  - Subtask\n    - **NOTE**: Important details\n      - ie. Examples"
                : "Paste your conversation here..."}
              placeholderTextColor={colors.textTertiary}
              value={rawTaskInput}
              onChangeText={setRawTaskInput}
              multiline
            />

            <View style={styles.buttonGroup}>
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={() => setStep(2)}
                disabled={!rawTaskInput.trim()}
              >
                <Text style={styles.primaryButtonText}>Next: Choose Template (Optional) →</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={handleGenerate}
                disabled={!rawTaskInput.trim()}
              >
                <Text style={styles.secondaryButtonText}>Generate Without Template</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* STEP 2: Template Selection (Optional) */}
        {step === 2 && (
          <View style={styles.stepContainer}>
            <Text style={[styles.stepTitle, { color: colors.text }]}>Step 2: Select Template (Optional)</Text>
            <Text style={[styles.stepDescription, { color: colors.textSecondary }]}>
              Choose a template to format your tasks, or skip to generate without one
            </Text>

            {templates.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                  No task templates yet. Create one in Templates and Projects, or generate without a template.
                </Text>
                <TouchableOpacity
                  style={styles.secondaryButton}
                  onPress={() => navigation.navigate('settings')}
                >
                  <Text style={styles.secondaryButtonText}>Go to Templates</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.templatesGrid}>
                {templates.map((template) => (
                  <TouchableOpacity
                    key={template.id}
                    style={[
                      styles.templateCard,
                      { backgroundColor: isDark ? colors.tertiary : colors.card, borderColor: colors.border },
                      selectedTemplate?.id === template.id && styles.templateCardSelected
                    ]}
                    onPress={() => {
                      setSelectedTemplate(template);
                      setStrictAdherence(strictAdherenceDefault);
                    }}
                  >
                    <Text style={[styles.templateName, { color: colors.text }]}>{template.name}</Text>
                    <Text style={[styles.templatePreview, { color: colors.textSecondary }]} numberOfLines={3}>
                      {template.content}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {selectedTemplate && (
              <View style={styles.strictAdherenceContainer}>
                <TouchableOpacity
                  style={styles.checkbox}
                  onPress={() => setStrictAdherence(!strictAdherence)}
                >
                  <View style={[styles.checkboxBox, { borderColor: colors.accent }, strictAdherence && styles.checkboxBoxChecked]}>
                    {strictAdherence && <Text style={styles.checkboxCheck}>✓</Text>}
                  </View>
                  <Text style={[styles.checkboxLabel, { color: colors.text }]}>Strict Template Adherence</Text>
                </TouchableOpacity>
                <Text style={[styles.checkboxDesc, { color: colors.textSecondary }]}>
                  Follow template structure exactly (recommended for consistent formatting)
                </Text>
              </View>
            )}

            <View style={styles.buttonGroup}>
              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={() => setStep(1)}
              >
                <Text style={styles.secondaryButtonText}>← Back</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={handleGenerate}
              >
                <Text style={styles.primaryButtonText}>
                  {selectedTemplate ? 'Generate with Template ✨' : 'Generate Tasks ✨'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* STEP 3: Generated Tasks with Sidebar Layout */}
        {step === 3 && (
          <View style={[styles.resultLayout, isMobile && styles.resultLayoutMobile]}>
            {/* Left Sidebar: Interactive Task List */}
            {!isMobile && showSidebar && (
              <View style={[styles.taskSidebar, { backgroundColor: colors.secondary, borderRightColor: colors.border }]}>
                <View style={[styles.sidebarHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.sidebarTitle, { color: colors.text }]}>Task List</Text>
                <TouchableOpacity
                    style={styles.collapseSidebarButton}
                    onPress={() => setShowSidebar(false)}
                >
                    <Text style={styles.collapseSidebarText}>←</Text>
                </TouchableOpacity>
            </View>

                {/* Loom Overview */}
            <View style={[styles.loomOverviewSection, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.loomOverviewLabel, { color: colors.accent }]}>Overview Video</Text>
              <TextInput
                style={[styles.loomOverviewInput, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                    placeholder="Paste Loom URL..."
                placeholderTextColor={colors.textTertiary}
                value={loomOverview}
                onChangeText={setLoomOverview}
              />
                </View>

                {/* Add Task Button */}
                <TouchableOpacity 
                  style={styles.addTaskButton}
                  onPress={addNewTask}
                >
                  <Text style={styles.addTaskButtonText}>+ Add Task</Text>
                </TouchableOpacity>

                {/* Task List with Up/Down Controls */}
                <ScrollView style={styles.taskListScroll} nestedScrollEnabled={true}>
                  {tasks.map((task, index) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      index={index}
                      totalTasks={tasks.length}
                      onToggle={() => toggleComplete(task.id)}
                      onUpdate={(updates) => updateTask(task.id, updates)}
                      onDelete={() => deleteTask(task.id)}
                      onMoveUp={() => moveTask(index, 'up')}
                      onMoveDown={() => moveTask(index, 'down')}
                      editing={editingTaskId === task.id}
                      setEditing={(editing) => setEditingTaskId(editing ? task.id : null)}
                    />
                  ))}
                </ScrollView>
            </View>
            )}

            {/* Show Sidebar Button (when collapsed) */}
            {!isMobile && !showSidebar && (
                        <TouchableOpacity 
                style={styles.showSidebarButton}
                onPress={() => setShowSidebar(true)}
              >
                <Text style={styles.showSidebarText}>→</Text>
                        </TouchableOpacity>
            )}

            {/* Center: Formatted Output */}
            <ScrollView 
              style={[styles.resultMainContent, isMobile && styles.resultMainContentMobile]}
              nestedScrollEnabled={true}
            >
              {/* Editable Header */}
              <View style={[styles.taskListHeaderSection, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.headerLabel, { color: colors.text }]}>Page Title:</Text>
                          <TextInput
                  style={[styles.headerInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                  value={taskListHeader}
                  onChangeText={setTaskListHeader}
                  placeholder="Task List Title"
                  placeholderTextColor={colors.textTertiary}
                />
                <Text style={[styles.headerLabel, { color: colors.text }]}>Overall Due Date:</Text>
                            <TextInput
                  style={[styles.dueDateInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                  value={overallDueDate}
                  onChangeText={setOverallDueDate}
                              placeholder="YYYY-MM-DD"
                              placeholderTextColor={colors.textTertiary}
                            />
                        </View>

              <View style={styles.resultHeader}>
                <Text style={[styles.resultTitle, { color: colors.text }]}>Generated Task List</Text>
                <View style={styles.resultActions}>
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => copyToClipboard('markdown')}
                    disabled={loading}
                  >
                    <Text style={styles.actionButtonText}>📋 Copy Markdown</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.notionButton]}
                    onPress={() => copyToClipboard('notion')}
                    disabled={loading}
                  >
                    <Text style={styles.actionButtonText}>📝 Copy for Notion</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Show streaming or final markdown */}
              <View style={[styles.markdownOutputBox, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                            <TextInput
                  style={[styles.markdownOutput, { color: colors.text }]}
                  value={loading && streamingMarkdown ? streamingMarkdown : formattedMarkdown}
                  onChangeText={setFormattedMarkdown}
                  multiline
                  editable={!loading}
                />
                {loading && (
                  <View style={styles.streamingIndicator}>
                    <Text style={styles.streamingText}>✨ Generating in real-time...</Text>
                  </View>
                          )}
                        </View>
            </ScrollView>

            {/* Right AI Assistant Sidebar (placeholder for now) */}
            {!isMobile && showAISidebar && (
              <View style={[styles.aiSidebarContainer, { backgroundColor: colors.secondary, borderLeftColor: colors.border }]}>
                <View style={[styles.sidebarHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.sidebarTitle, { color: colors.text }]}>AI Suggestions</Text>
                                <TouchableOpacity
                    style={styles.collapseSidebarButton}
                    onPress={() => setShowAISidebar(false)}
                  >
                    <Text style={styles.collapseSidebarText}>→</Text>
                  </TouchableOpacity>
                </View>

                <ScrollView style={styles.aiSuggestionsList} nestedScrollEnabled={true}>
                  {loadingSuggestions && (
                    <View style={{ padding: 20, alignItems: 'center' }}>
                      <ActivityIndicator size="small" color={colors.accent} />
                      <Text style={[styles.aiPlaceholderText, { marginTop: 10, color: colors.textSecondary }]}>
                        Analyzing your tasks...
                      </Text>
                    </View>
                  )}
                  
                  {!loadingSuggestions && aiSuggestions.length === 0 && (
                    <Text style={[styles.aiPlaceholderText, { color: colors.textSecondary }]}>
                      AI suggestions will analyze your tasks for clarity, missing details, and opportunities for better organization.
                    </Text>
                  )}
                  
                  {!loadingSuggestions && aiSuggestions.map((suggestion) => (
                    !dismissedSuggestions.includes(suggestion.id) && (
                      <View key={suggestion.id} style={[styles.aiSuggestionCard, { backgroundColor: colors.card }]}>
                        <Text style={styles.aiSuggestionTitle}>{suggestion.title}</Text>
                        <Text style={[styles.aiSuggestionText, { color: colors.text }]}>
                          {suggestion.suggestion}
                        </Text>
                        <View style={styles.aiSuggestionActions}>
                          <TouchableOpacity 
                            style={[styles.aiActionButton, styles.aiActionButtonSecondary]}
                            onPress={() => handleDismissSuggestion(suggestion.id)}
                          >
                            <Text style={styles.aiActionButtonText}>✕ Ignore</Text>
                          </TouchableOpacity>
                          <TouchableOpacity 
                            style={[styles.aiActionButton, styles.aiActionButtonPrimary]}
                            onPress={() => handleApplySuggestion(suggestion.id, suggestion.suggestion)}
                            disabled={loading}
                          >
                            <Text style={styles.aiActionButtonText}>{loading ? '⏳' : '✨'} Apply</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )
                  ))}
                </ScrollView>
                      </View>
            )}

            {/* Show AI Sidebar Button (when collapsed) */}
            {!isMobile && !showAISidebar && (
                        <TouchableOpacity
                style={styles.showRightSidebarButton}
                onPress={() => setShowAISidebar(true)}
              >
                <Text style={styles.showSidebarText}>← 🤖</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </ScrollView>

      {/* Progress Indicator */}
      <View style={[styles.bottomProgressContainer, { backgroundColor: colors.card, borderTopColor: colors.border }]}>
        <View style={styles.progressBar}>
          {[1, 2, 3].map((s) => (
            <View 
              key={s} 
              style={[
                styles.progressStep,
                { backgroundColor: colors.textSecondary },
                s <= step && styles.progressStepActive
              ]} 
            />
          ))}
        </View>
        <Text style={[styles.progressText, { color: colors.textSecondary }]}>Step {step} of 3</Text>
      </View>
      
      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onHide={() => setToast({ ...toast, visible: false })}
      />
    </View>
  );
}

// Task Item Component
const TaskItem = ({ task, index, totalTasks, onToggle, onUpdate, onDelete, onMoveUp, onMoveDown, editing, setEditing }) => {
  const { colors, glass } = useTheme();

  return (
    <View style={[styles.taskCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }, task.completed && styles.taskCardCompleted]}>
      <View style={styles.taskHeader}>
        <TouchableOpacity onPress={onToggle} style={styles.checkboxTask}>
          <View style={[styles.checkboxTaskBox, { borderColor: colors.accent }, task.completed && [styles.checkboxTaskBoxChecked, { backgroundColor: colors.accent, borderColor: colors.accent }]]}>
            {task.completed && <Text style={styles.checkboxTaskCheck}>✓</Text>}
          </View>
        </TouchableOpacity>

        {editing ? (
          <TextInput
            style={[styles.taskDescriptionInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
            value={task.description}
            onChangeText={(text) => onUpdate({ description: text })}
            multiline
          />
        ) : (
          <Text style={[styles.taskDescription, { color: colors.text }, task.completed && styles.taskDescriptionCompleted]}>
            {task.description}
                          </Text>
        )}

        {/* Move Up/Down Controls */}
        <View style={styles.moveControls}>
          <TouchableOpacity 
            onPress={onMoveUp} 
            disabled={index === 0}
            style={[styles.moveButton, { backgroundColor: colors.card }, index === 0 && styles.moveButtonDisabled]}
          >
            <Text style={[styles.moveButtonText, { color: colors.accent }, index === 0 && styles.moveButtonTextDisabled]}>↑</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
            onPress={onMoveDown} 
            disabled={index === totalTasks - 1}
            style={[styles.moveButton, { backgroundColor: colors.card }, index === totalTasks - 1 && styles.moveButtonDisabled]}
                        >
            <Text style={[styles.moveButtonText, { color: colors.accent }, index === totalTasks - 1 && styles.moveButtonTextDisabled]}>↓</Text>
                        </TouchableOpacity>
                      </View>
                    </View>

      {!editing && (task.dueDate || task.estimatedHours || task.priority) && (
        <View style={styles.taskMeta}>
          {task.priority && (
            <View style={[styles.priorityBadge, styles[`priority${task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}`]]}>
              <Text style={styles.priorityBadgeText}>{task.priority}</Text>
              </View>
          )}
          {task.dueDate && <Text style={[styles.taskMetaText, { color: colors.textSecondary }]}>Due: {task.dueDate}</Text>}
          {task.estimatedHours && <Text style={[styles.taskMetaText, { color: colors.textSecondary }]}>Est: {task.estimatedHours}h</Text>}
          </View>
        )}

      {/* Notes Section */}
      <View style={styles.taskNotesSection}>
        <Text style={[styles.taskNotesLabel, { color: colors.textSecondary }]}>Notes (will appear as "**NOTE**:" in output):</Text>
        <TextInput
          style={[styles.taskNotesInput, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          value={task.notes || ''}
          onChangeText={(text) => onUpdate({ notes: text })}
          placeholder="Add clarifying notes or details..."
          placeholderTextColor={colors.textTertiary}
          multiline
        />
      </View>

      <View style={styles.taskActions}>
        <TouchableOpacity onPress={() => setEditing(!editing)}>
          <Text style={[styles.taskActionText, { color: colors.accent }]}>{editing ? 'Done' : 'Edit'}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onDelete}>
          <Text style={[styles.taskActionText, styles.taskActionDelete]}>Delete</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingTop: 120,
    paddingBottom: 100,
    flexGrow: 1,
  },
  contentContainerFull: {
    flexGrow: 1,
    paddingTop: 20,
    paddingBottom: 100,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#4a9eff',
    marginTop: 15,
    fontSize: 16,
  },
  resetButton: {
    padding: 8,
    marginRight: 10,
  },
  resetButtonText: {
    color: '#4a9eff',
    fontSize: 14,
  },
  stepContainer: {
    width: '100%',
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    marginTop: 40,
    marginBottom: 10,
  },
  stepDescription: {
    fontSize: 14,
    marginBottom: 30,
    lineHeight: 22,
  },
  textArea: {
    borderRadius: 12,
    padding: 15,
    fontSize: 14,
    minHeight: 300,
    textAlignVertical: 'top',
    borderWidth: 1,
    marginBottom: 20,
    fontFamily: 'monospace',
  },
  buttonGroup: {
    gap: 10,
  },
  primaryButton: {
    backgroundColor: '#4a9eff',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#4a9eff',
  },
  secondaryButtonText: {
    color: '#4a9eff',
    fontSize: 16,
    fontWeight: '600',
  },
  inputModeContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
    marginTop: 20,
  },
  inputModeButton: {
    flex: 1,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
  },
  inputModeButtonActive: {
    borderColor: '#4a9eff',
    borderWidth: 2,
  },
  inputModeButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  inputModeButtonTextActive: {
    color: '#4a9eff',
  },
  inputModeHint: {
    fontSize: 12,
    marginBottom: 15,
    lineHeight: 18,
  },
  templatesGrid: {
    gap: 15,
    marginBottom: 20,
  },
  templateCard: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
  },
  templateCardSelected: {
    borderColor: '#4a9eff',
    borderWidth: 2,
  },
  templateName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  templatePreview: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: 'monospace',
  },
  strictAdherenceContainer: {
    marginBottom: 20,
  },
  checkbox: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  checkboxBox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxBoxChecked: {
    backgroundColor: '#4a9eff',
  },
  checkboxCheck: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 16,
  },
  checkboxDesc: {
    fontSize: 12,
    marginLeft: 34,
    opacity: 0.6,
  },
  emptyState: {
    padding: 40,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 24,
    opacity: 0.6,
  },
  // STEP 3: Result Layout
  resultLayout: {
    flexDirection: 'row',
    flex: 1,
    minHeight: 600,
  },
  resultLayoutMobile: {
    flexDirection: 'column',
  },
  // Left Sidebar
  taskSidebar: {
    width: 350,
    borderRightWidth: 1,
    padding: 15,
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    paddingBottom: 15,
    borderBottomWidth: 1,
  },
  sidebarTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  collapseSidebarButton: {
    padding: 5,
  },
  collapseSidebarText: {
    fontSize: 20,
    color: '#4a9eff',
  },
  showSidebarButton: {
    position: 'absolute',
    left: 0,
    top: '50%',
    backgroundColor: '#4a9eff',
    padding: 10,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    zIndex: 100,
  },
  showSidebarText: {
    color: '#ffffff',
    fontSize: 18,
  },
  showRightSidebarButton: {
    position: 'absolute',
    right: 0,
    top: '50%',
    backgroundColor: '#6a5aff',
    padding: 12,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    zIndex: 100,
  },
  loomOverviewSection: {
    marginBottom: 15,
    paddingBottom: 15,
    borderBottomWidth: 1,
  },
  addTaskButton: {
    backgroundColor: '#4a9eff',
    borderRadius: 6,
    padding: 12,
    alignItems: 'center',
    marginBottom: 15,
  },
  addTaskButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  loomOverviewLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6a5aff',
    marginBottom: 8,
  },
  loomOverviewInput: {
    borderRadius: 6,
    padding: 10,
    fontSize: 13,
    borderWidth: 1,
  },
  taskListScroll: {
    flex: 1,
  },
  // Task Card in Sidebar
  taskCard: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
  },
  taskCardCompleted: {
    opacity: 0.5,
  },
  taskHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 8,
  },
  checkboxTask: {
    padding: 2,
  },
  checkboxTaskBox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxTaskBoxChecked: {
    backgroundColor: '#4a9eff',
  },
  checkboxTaskCheck: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  taskDescription: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  taskDescriptionCompleted: {
    textDecorationLine: 'line-through',
    opacity: 0.5,
  },
  taskDescriptionInput: {
    flex: 1,
    borderRadius: 4,
    padding: 8,
    fontSize: 14,
    borderWidth: 1,
    borderColor: '#4a9eff',
    minHeight: 40,
  },
  moveControls: {
    flexDirection: 'column',
    gap: 2,
  },
  moveButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#4a9eff',
  },
  moveButtonDisabled: {
    opacity: 0.3,
  },
  moveButtonText: {
    color: '#4a9eff',
    fontSize: 14,
    fontWeight: 'bold',
    lineHeight: 14,
  },
  moveButtonTextDisabled: {
    opacity: 0.3,
  },
  taskMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
    paddingLeft: 28,
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  priorityHigh: {
    backgroundColor: '#ff4444',
  },
  priorityMedium: {
    backgroundColor: '#ffa94d',
  },
  priorityLow: {
    backgroundColor: '#4a9eff',
  },
  priorityBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  taskMetaText: {
    fontSize: 12,
    opacity: 0.6,
  },
  taskActions: {
    flexDirection: 'row',
    gap: 15,
    paddingLeft: 28,
  },
  taskActionText: {
    fontSize: 13,
    color: '#4a9eff',
    fontWeight: '600',
  },
  taskActionDelete: {
    color: '#ff4444',
  },
  // Center Content
  resultMainContent: {
    flex: 1,
    padding: 20,
  },
  resultMainContentMobile: {
    padding: 15,
  },
  resultHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 10,
  },
  resultTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  resultActions: {
    flexDirection: 'row',
    gap: 10,
  },
  actionButton: {
    backgroundColor: '#4a9eff',
    padding: 10,
    borderRadius: 6,
  },
  notionButton: {
    backgroundColor: '#6a5aff',
  },
  actionButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  markdownOutputBox: {
    borderRadius: 12,
    padding: 20,
    marginBottom: 30,
    borderWidth: 1,
    minHeight: 500,
    flex: 1,
  },
  markdownOutput: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'monospace',
    minHeight: 500,
  },
  streamingIndicator: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: 'rgba(74, 158, 255, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#4a9eff',
  },
  streamingText: {
    color: '#4a9eff',
    fontSize: 12,
    fontWeight: '600',
  },
  taskListHeaderSection: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
  },
  headerLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 8,
    marginTop: 8,
    opacity: 0.6,
  },
  headerInput: {
    borderWidth: 1,
    borderColor: '#4a9eff',
    borderRadius: 8,
    padding: 12,
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  dueDateInput: {
    borderWidth: 1,
    borderColor: '#4a9eff',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
  },
  taskNotesSection: {
    marginTop: 12,
    marginBottom: 8,
  },
  taskNotesLabel: {
    fontSize: 11,
    marginBottom: 6,
    opacity: 0.6,
  },
  taskNotesInput: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
    fontSize: 13,
    minHeight: 60,
  },
  // AI Assistant Sidebar (Right)
  aiSidebarContainer: {
    width: 320,
    borderLeftWidth: 1,
    padding: 15,
  },
  aiSuggestionsList: {
    flex: 1,
  },
  aiSuggestionCard: {
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#6a5aff',
  },
  aiSuggestionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#6a5aff',
    marginBottom: 8,
  },
  aiSuggestionText: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 12,
    opacity: 0.8,
  },
  aiSuggestionActions: {
    flexDirection: 'row',
    gap: 8,
  },
  aiActionButton: {
    flex: 1,
    padding: 8,
    borderRadius: 6,
    alignItems: 'center',
  },
  aiActionButtonSecondary: {
    backgroundColor: '#ff4444',
  },
  aiActionButtonPrimary: {
    backgroundColor: '#6a5aff',
  },
  aiActionButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  aiPlaceholderText: {
    fontSize: 12,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 20,
    lineHeight: 18,
    opacity: 0.5,
  },
  bottomProgressContainer: {
    paddingVertical: 12,
    paddingHorizontal: 15,
    alignItems: 'center',
    borderTopWidth: 1,
  },
  progressBar: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  progressStep: {
    width: 60,
    height: 4,
    borderRadius: 2,
    opacity: 0.3,
  },
  progressStepActive: {
    backgroundColor: '#4a9eff',
    opacity: 1,
  },
  progressText: {
    fontSize: 12,
    opacity: 0.6,
  },
});
